"use client"

import { useEffect, useRef } from "react"
import authService from "@/lib/services/auth-service"

/**
 * Orquestra a retomada do app ao voltar do segundo plano.
 *
 * A shell nativa (Expo/react-native-webview), ao detectar AppState "active",
 * injeta uma mensagem `{ type: "APP_FOREGROUND", backgroundMs }`. No browser puro
 * (PWA sem a shell, ou caso o sinal nativo falhe) usamos `visibilitychange` como
 * fallback, medindo quanto tempo a aba ficou escondida.
 *
 * Ao voltar de um background relevante:
 *  1. Renova o token Cognito proativamente (após muito tempo parado o access
 *     token. TTL ~1h, pode ter expirado; renovar agora evita o 401 + redirect
 *     pra /login no meio da primeira ação do usuário, que era percebido como
 *     "travou, tive que fechar e abrir").
 *  2. Dispara `welltrainer:foreground` pra que o resto do app (AuthGuard, hooks
 *     de dados/conexões) ressincronize em vez de confiar que o WebView preservou
 *     tudo intacto durante o background.
 */

// Acima disso vale ressincronizar dados ao voltar (sessão, notificações, treino…).
const FOREGROUND_EVENT_MS = 20_000
// Acima disso renova o token proativamente (ver item 1 acima).
const TOKEN_REFRESH_MS = 300_000
// Dedupe: APP_FOREGROUND (nativo) e visibilitychange (web) disparam juntos no
// mesmo retorno, só processamos o primeiro.
const DEDUPE_MS = 4_000

export function ForegroundResume() {
  const lastResumeRef = useRef(0)
  const hiddenSinceRef = useRef<number | null>(null)

  useEffect(() => {
    let cancelled = false

    const aoVoltar = (backgroundMs: number) => {
      const agora = Date.now()
      if (agora - lastResumeRef.current < DEDUPE_MS) return
      lastResumeRef.current = agora

      if (backgroundMs < FOREGROUND_EVENT_MS) return

      const avisarApp = () => {
        if (cancelled) return
        window.dispatchEvent(
          new CustomEvent("welltrainer:foreground", { detail: { backgroundMs } }),
        )
      }

      if (backgroundMs >= TOKEN_REFRESH_MS && authService.isAuthenticated()) {
        // Renova a sessão ANTES de avisar o app, pra que os refetches disparados
        // pelo evento já usem um token válido.
        authService
          .forceRefreshToken()
          .catch(() => {})
          .finally(avisarApp)
      } else {
        avisarApp()
      }
    }

    // Sinal da shell nativa (Expo) ao voltar do segundo plano.
    const onMessage = (event: MessageEvent) => {
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data
        if (data?.type === "APP_FOREGROUND") {
          aoVoltar(
            typeof data.backgroundMs === "number" ? data.backgroundMs : TOKEN_REFRESH_MS,
          )
        }
      } catch {}
    }

    // Fallback puro web (PWA no browser, ou se o sinal nativo se perder).
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now()
      } else if (document.visibilityState === "visible") {
        const bg = hiddenSinceRef.current
          ? Date.now() - hiddenSinceRef.current
          : 0
        hiddenSinceRef.current = null
        aoVoltar(bg)
      }
    }

    window.addEventListener("message", onMessage)
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      cancelled = true
      window.removeEventListener("message", onMessage)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [])

  return null
}
